<template>
  <div class="importer-page">
    <section>
      <div class="py-8 px-8">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h4 class="mb-1">Importer</h4>
            <p class="text-muted mb-0">Upload Excel or CSV files, map columns to GStocky fields, preview, then import.</p>
          </div>
          <button class="btn btn-outline-secondary btn-sm" @click="resetAll">
            <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />
            Reset
          </button>
        </div>
      </div>
    </section>

    <section class="px-8 pb-5">
      <div class="row g-3">
        <div class="col-12 col-lg-3">
          <div class="import-panel p-3">
            <label class="form-label fw-semibold">Data type</label>
            <div class="list-group import-types">
              <button
                v-for="type in importTypes"
                :key="type.key"
                type="button"
                class="list-group-item list-group-item-action d-flex align-items-center"
                :class="{ active: selectedType === type.key }"
                @click="selectType(type.key)"
              >
                <AppIcon :name="'bi ' + type.icon + ' me-2'" :class="'bi ' + type.icon + ' me-2'" />
                <span>{{ type.label }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-9">
          <div class="import-panel p-3 mb-3">
            <div class="row align-items-end g-3">
              <div class="col-12 col-md-8">
                <label class="form-label fw-semibold">File</label>
                <input ref="fileInput" class="form-control" type="file" accept=".csv,.xls,.xlsx" @change="uploadFile">
                <small class="text-muted">Accepted formats: CSV, XLS, XLSX. The first row must contain column names.</small>
              </div>
              <div class="col-12 col-md-4">
                <div class="summary-box">
                  <div class="small text-muted">Rows ready</div>
                  <div class="h4 mb-0">{{ mappedRows.length }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="headers.length" class="import-panel p-3 mb-3">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h6 class="mb-1">Column mapping</h6>
                <small class="text-muted">We guessed the closest fields. Adjust anything that looks off before importing.</small>
              </div>
              <button class="btn btn-sm btn-outline-primary" @click="autoMapHeaders">
                <AppIcon name="bi-magic" class="bi bi-magic me-1" />
                Auto map
              </button>
            </div>

            <div class="row g-3">
              <div v-for="field in currentConfig.fields" :key="field.key" class="col-12 col-md-6 col-xl-4">
                <label class="form-label small mb-1">
                  {{ field.label }}
                  <span v-if="field.required" class="text-danger">*</span>
                </label>
                <select v-model="mapping[field.key]" class="form-select form-select-sm">
                  <option value="">Do not import</option>
                  <option v-for="header in headers" :key="field.key + header" :value="header">{{ header }}</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="headers.length" class="import-panel p-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h6 class="mb-1">Preview</h6>
                <small class="text-muted">{{ validationSummary }}</small>
              </div>
              <button class="btn btn-success" :disabled="saving || !validRows.length" @click="saveRows">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-1" />
                Import {{ validRows.length }} rows
              </button>
            </div>

            <div v-if="invalidRows.length" class="alert alert-warning py-2">
              {{ invalidRows.length }} row(s) are missing required fields and will be skipped.
            </div>

            <div class="table-responsive preview-table">
              <table class="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th v-for="field in currentConfig.fields" :key="field.key">{{ field.label }}</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in previewRows" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td v-for="field in currentConfig.fields" :key="field.key">
                      <input
                        :value="row.data[field.key]"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': row.missing.includes(field.key) }"
                        @input="applyPreviewEdit(index, field.key, $event.target.value)"
                      >
                    </td>
                    <td>
                      <span v-if="row.valid" class="badge bg-success">Ready</span>
                      <span v-else class="badge bg-warning text-dark">Missing {{ row.missingLabels.join(', ') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="empty-state text-center p-5">
            <AppIcon name="bi-file-earmark-arrow-up" class="bi bi-file-earmark-arrow-up" />
            <h5 class="mt-3">Start with a file</h5>
            <p class="text-muted mb-0">Choose the data type, then upload an Excel or CSV file to map your columns.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { read, utils } from 'xlsx'
import helper from '../helper'

const normalize = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '')

const today = () => new Date().toISOString().slice(0, 10)

export default {
  name: 'Importer',
  data() {
    return {
      selectedType: 'clients',
      headers: [],
      rawRows: [],
      mapping: {},
      rowOverrides: {},
      saving: false,
      importTypes: [
        { key: 'clients', label: 'Clients', icon: 'bi-people' },
        { key: 'suppliers', label: 'Suppliers', icon: 'bi-person-lines-fill' },
        { key: 'products', label: 'Products', icon: 'bi-box2' },
        { key: 'invoices', label: 'Invoices', icon: 'bi-file-ruled' },
        { key: 'deliveries', label: 'Deliveries', icon: 'bi-truck' },
        { key: 'supplier_invoices', label: 'Supplier invoices', icon: 'bi-receipt' }
      ],
      configs: {
        clients: {
          endpoint: '/clients/bulk',
          payloadKey: 'clients',
          fields: [
            { key: 'name', label: 'Name', required: true, aliases: ['name', 'client', 'client name', 'customer', 'nom', 'full name'] },
            { key: 'email', label: 'Email', type: 'string', aliases: ['email', 'mail', 'e-mail'] },
            { key: 'phone', label: 'Phone', type: 'string', aliases: ['phone', 'telephone', 'tel', 'mobile'] },
            { key: 'address', label: 'Address', type: 'string', aliases: ['address', 'adresse'] },
            { key: 'city', label: 'City', type: 'string', aliases: ['city', 'ville'] },
            { key: 'country', label: 'Country', type: 'string', aliases: ['country', 'pays'] },
            { key: 'tax_id', label: 'Tax ID', type: 'string', aliases: ['tax id', 'tax_id', 'ice', 'if', 'identifiant fiscal'] },
            { key: 'vat_number', label: 'VAT number', type: 'string', aliases: ['vat', 'vat number', 'tva'] }
          ]
        },
        suppliers: {
          endpoint: '/suppliers/bulk',
          payloadKey: 'suppliers',
          fields: [
            { key: 'name', label: 'Name', required: true, aliases: ['name', 'supplier', 'supplier name', 'fournisseur', 'nom'] },
            { key: 'email', label: 'Email', type: 'string', aliases: ['email', 'mail', 'e-mail'] },
            { key: 'phone', label: 'Phone', type: 'string', aliases: ['phone', 'telephone', 'tel', 'mobile'] },
            { key: 'address', label: 'Address', type: 'string', aliases: ['address', 'adresse'] },
            { key: 'city', label: 'City', type: 'string', aliases: ['city', 'ville'] },
            { key: 'country', label: 'Country', type: 'string', aliases: ['country', 'pays'] },
            { key: 'tax_id', label: 'Tax ID', type: 'string', aliases: ['tax id', 'tax_id', 'ice', 'if', 'identifiant fiscal'] },
            { key: 'vat_number', label: 'VAT number', type: 'string', aliases: ['vat', 'vat number', 'tva'] }
          ]
        },
        products: {
          endpoint: '/products/bulk',
          payloadKey: 'products',
          fields: [
            { key: 'label', label: 'Label', required: true, aliases: ['label', 'product', 'product name', 'name', 'designation', 'article'] },
            { key: 'quantity', label: 'Quantity', required: true, type: 'number', aliases: ['quantity', 'qty', 'qte', 'stock'] },
            { key: 'purchase_price', label: 'Purchase price', required: true, type: 'number', aliases: ['purchase price', 'cost', 'cost price', 'prix achat', 'purchase_price'] },
            { key: 'price', label: 'Sale price', required: true, type: 'number', aliases: ['price', 'sale price', 'selling price', 'prix vente'] },
            { key: 'sku', label: 'SKU', aliases: ['sku', 'reference', 'ref'] },
            { key: 'created_at', label: 'Created at', aliases: ['created at', 'created_at', 'date'] }
          ]
        },
        invoices: {
          endpoint: '/invoices',
          fields: [
            { key: 'reference', label: 'Reference', aliases: ['reference', 'ref', 'invoice', 'invoice number'] },
            { key: 'date', label: 'Date', required: true, aliases: ['date', 'invoice date'] },
            { key: 'client_id', label: 'Client ID', aliases: ['client id', 'client_id', 'customer id'] },
            { key: 'product_label', label: 'Product label', required: true, aliases: ['product', 'product label', 'product_label', 'item', 'designation'] },
            { key: 'quantity', label: 'Quantity', required: true, type: 'number', aliases: ['quantity', 'qty', 'qte'] },
            { key: 'price', label: 'Price', required: true, type: 'number', aliases: ['price', 'unit price', 'prix'] },
            { key: 'vat', label: 'VAT %', type: 'number', aliases: ['vat', 'tva', 'tax'] },
            { key: 'discount', label: 'Discount %', type: 'number', aliases: ['discount', 'remise'] },
            { key: 'status', label: 'Status', aliases: ['status', 'statut'] },
            { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'description'] }
          ]
        },
        deliveries: {
          endpoint: '/deliveries',
          fields: [
            { key: 'reference', label: 'Reference', required: true, aliases: ['reference', 'ref', 'delivery', 'delivery number', 'bon livraison'] },
            { key: 'date', label: 'Date', required: true, aliases: ['date', 'delivery date'] },
            { key: 'client_id', label: 'Client ID', aliases: ['client id', 'client_id', 'customer id'] },
            { key: 'product_label', label: 'Product label', required: true, aliases: ['product', 'product label', 'product_label', 'item', 'designation'] },
            { key: 'quantity', label: 'Quantity', required: true, type: 'number', aliases: ['quantity', 'qty', 'qte'] },
            { key: 'price', label: 'Price', required: true, type: 'number', aliases: ['price', 'unit price', 'prix'] },
            { key: 'vat', label: 'VAT %', type: 'number', aliases: ['vat', 'tva', 'tax'] },
            { key: 'status', label: 'Status', aliases: ['status', 'statut'] },
            { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'description'] }
          ]
        },
        supplier_invoices: {
          endpoint: '/supplier-invoices',
          fields: [
            { key: 'reference', label: 'Reference', required: true, aliases: ['reference', 'ref', 'invoice', 'supplier invoice'] },
            { key: 'date', label: 'Date', required: true, aliases: ['date', 'invoice date'] },
            { key: 'supplier_id', label: 'Supplier ID', aliases: ['supplier id', 'supplier_id', 'vendor id'] },
            { key: 'product_label', label: 'Product label', required: true, aliases: ['product', 'product label', 'product_label', 'item', 'designation'] },
            { key: 'quantity', label: 'Quantity', required: true, type: 'number', aliases: ['quantity', 'qty', 'qte'] },
            { key: 'price', label: 'Price', required: true, type: 'number', aliases: ['price', 'unit price', 'prix'] },
            { key: 'vat', label: 'VAT %', type: 'number', aliases: ['vat', 'tva', 'tax'] },
            { key: 'discount', label: 'Discount %', type: 'number', aliases: ['discount', 'remise'] },
            { key: 'status', label: 'Status', aliases: ['status', 'statut'] },
            { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'description'] }
          ]
        }
      }
    }
  },
  computed: {
    currentConfig() {
      return this.configs[this.selectedType]
    },
    mappedRows() {
      return this.rawRows.map((row, index) => {
        const data = {}
        this.currentConfig.fields.forEach(field => {
          const header = this.mapping[field.key]
          let value = header ? row[header] : ''
          if (field.type === 'number') value = this.toNumber(value)
          if (field.type === 'string') value = this.toStringValue(value)
          data[field.key] = value === undefined || value === null ? '' : value
        })
        if (!data.date && ['invoices', 'deliveries', 'supplier_invoices'].includes(this.selectedType)) data.date = today()
        if (!data.reference && ['deliveries', 'supplier_invoices'].includes(this.selectedType)) data.reference = `${this.selectedType.replace('_', '-').toUpperCase()}-${Date.now()}-${index + 1}`
        const override = this.rowOverrides[index] || {}
        Object.keys(override).forEach(key => {
          data[key] = override[key]
        })
        const missing = this.currentConfig.fields.filter(field => field.required && !this.hasValue(data[field.key])).map(field => field.key)
        return {
          data,
          missing,
          missingLabels: missing.map(key => this.currentConfig.fields.find(field => field.key === key).label),
          valid: missing.length === 0
        }
      })
    },
    validRows() {
      return this.mappedRows.filter(row => row.valid).map(row => row.data)
    },
    invalidRows() {
      return this.mappedRows.filter(row => !row.valid)
    },
    previewRows() {
      return this.mappedRows.slice(0, 100)
    },
    validationSummary() {
      if (!this.mappedRows.length) return 'No rows found.'
      return `${this.validRows.length} ready, ${this.invalidRows.length} need attention. Preview is limited to 100 rows.`
    }
  },
  methods: {
    selectType(type) {
      if (this.selectedType === type) return
      this.selectedType = type
      this.resetImportData()
    },
    resetAll() {
      this.resetImportData()
    },
    resetImportData() {
      this.headers = []
      this.rawRows = []
      this.mapping = {}
      this.rowOverrides = {}
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    uploadFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = e => {
        const workbook = read(e.target.result, { type: 'binary', cellDates: true })
        const firstSheet = workbook.SheetNames[0]
        const rows = utils.sheet_to_json(workbook.Sheets[firstSheet], { defval: '' })
        this.rawRows = rows
        this.headers = rows.length ? Object.keys(rows[0]) : []
        this.autoMapHeaders()
      }
      reader.readAsBinaryString(file)
    },
    autoMapHeaders() {
      const nextMapping = {}
      this.currentConfig.fields.forEach(field => {
        const aliases = [field.key, field.label].concat(field.aliases || []).map(normalize)
        const exact = this.headers.find(header => aliases.includes(normalize(header)))
        const partial = this.headers.find(header => aliases.some(alias => normalize(header).includes(alias) || alias.includes(normalize(header))))
        nextMapping[field.key] = exact || partial || ''
      })
      this.mapping = nextMapping
      this.rowOverrides = {}
    },
    applyPreviewEdit(index, key, value) {
      this.$set(this.rowOverrides, index, {
        ...(this.rowOverrides[index] || {}),
        [key]: value
      })
    },
    hasValue(value) {
      return value !== undefined && value !== null && String(value).trim() !== ''
    },
    toNumber(value) {
      if (value === '' || value === null || value === undefined) return 0
      const normalized = String(value).replace(',', '.').replace(/[^0-9.-]/g, '')
      const number = Number(normalized)
      return Number.isFinite(number) ? number : 0
    },
    toStringValue(value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },
    buildDocumentPayload(row) {
      const quantity = this.toNumber(row.quantity) || 1
      const price = this.toNumber(row.price)
      const vat = this.toNumber(row.vat)
      const discount = this.toNumber(row.discount)
      const subTotal = quantity * price
      const total = subTotal - (subTotal * discount / 100) + ((subTotal - (subTotal * discount / 100)) * vat / 100)
      const product = {
        inventory_id: null,
        product_id: null,
        custom_product: true,
        product_label: row.product_label,
        quantity,
        price,
        purchase_price: row.purchase_price || 0,
        vat,
        discount,
        sub_total: subTotal,
        total,
        unity: null
      }

      const payload = {
        reference: row.reference || null,
        date: row.date || today(),
        status: row.status || 'pending',
        notes: row.notes || '',
        products: [product]
      }

      if (this.selectedType === 'invoices') payload.client_id = row.client_id || null
      if (this.selectedType === 'deliveries') payload.client_id = row.client_id || null
      if (this.selectedType === 'supplier_invoices') {
        payload.supplier_id = row.supplier_id || null
        payload.amount_paid = '0'
      }

      return payload
    },
    async saveRows() {
      if (!this.validRows.length) return
      this.saving = true
      try {
        const config = this.currentConfig
        if (config.payloadKey) {
          await helper.customPost(`${process.env.VUE_APP_API}${config.endpoint}`, { [config.payloadKey]: this.validRows }, { showToast: true })
        } else {
          for (let i = 0; i < this.validRows.length; i++) {
            await helper.customPost(`${process.env.VUE_APP_API}${config.endpoint}`, this.buildDocumentPayload(this.validRows[i]))
          }
          window.vueOpener.open({
            message: 'Import completed successfully',
            type: 'success',
            position: 'bottom'
          })
        }
        this.resetAll()
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.import-panel,
.empty-state {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.import-types .list-group-item {
  border-color: #edf0f2;
  font-weight: 600;
}

.import-types .list-group-item.active {
  background: #303030;
  border-color: #303030;
}

.summary-box {
  border: 1px solid #edf0f2;
  border-radius: 8px;
  padding: 0.75rem;
  background: #f8f9fa;
}

.preview-table {
  max-height: 520px;
}

.preview-table th,
.preview-table td {
  white-space: nowrap;
  vertical-align: middle;
}

.preview-table input {
  min-width: 130px;
}

.empty-state i {
  font-size: 2.5rem;
  color: #6c757d;
}
</style>
